import { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CheckCircle,
  Download,
  AlertCircle,
  Printer,
  CreditCard,
  Users,
  TrendingUp,
} from "lucide-react";
import DataTable from "@/components/datatable/Datatable";
import { ColumnDef } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";

interface DeductionRow {
  declarationId: number;
  memberId: number;
  memberCode: string | null;
  memberName: string | null;
  phone: string | null;
  declaredAmount: number;
  minimumAmount: number;
  month: number;
  year: number;
  status: string;
  payrollStatus: string;
  approvalCount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  sentToHrAt: string | null;
}

interface DeductionsResponse {
  success: boolean;
  data: DeductionRow[];
  month: number;
  year: number;
  batchStatus: string | null;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PAYROLL_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ready_for_hr: { label: "Ready for HR", color: "bg-blue-100 text-blue-700" },
  sent_to_hr: { label: "Sent to HR", color: "bg-purple-100 text-purple-700" },
  deducted: { label: "Deducted", color: "bg-green-100 text-green-700" },
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700" },
  failed: { label: "Failed", color: "bg-red-100 text-red-700" },
};

export default function MonthlySavingsPayroll({
  selectedMonth,
  selectedYear,
}: {
  selectedMonth: number;
  selectedYear: number;
}) {
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [sorting, setSorting] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [searchQuery, setSearchQuery] = useState("");

  const markDeductedModal = useConfirmModal();
  const bulkMarkDeductedModal = useConfirmModal();

  const { data: deductionsData, isLoading, error } = useQuery<DeductionsResponse>(
    ["monthly-savings-deductions", selectedMonth, selectedYear],
    async () => {
      const { data } = await api.get("/payroll/monthly-saving-deductions", {
        params: { month: selectedMonth, year: selectedYear },
      });
      return data;
    }
  );

  const deductions = deductionsData?.data || [];

  const markSingleMutation = useMutation(
    async (declarationId: number) => {
      const { data } = await api.patch(`/payroll/monthly-saving-deductions/${declarationId}/mark-deducted`);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Declaration marked as deducted");
        queryClient.invalidateQueries(["monthly-savings-deductions", selectedMonth, selectedYear]);
        markDeductedModal.close();
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.message || err.message || "Failed to mark as deducted");
        markDeductedModal.close();
      },
    }
  );

  const bulkMarkMutation = useMutation(
    async () => {
      const pendingIds = deductions
        .filter((d) => d.payrollStatus !== "deducted")
        .map((d) => d.declarationId);
      if (!pendingIds.length) {
        throw new Error("No pending declarations to mark as deducted");
      }
      const { data } = await api.post("/payroll/monthly-saving-deductions/bulk-mark-deducted", { ids: pendingIds });
      return data;
    },
    {
      onSuccess: (res) => {
        toast.success(res.message || "Bulk deduction processed");
        queryClient.invalidateQueries(["monthly-savings-deductions", selectedMonth, selectedYear]);
        bulkMarkDeductedModal.close();
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.message || err.message || "Bulk deduction failed");
        bulkMarkDeductedModal.close();
      },
    }
  );

  const handleMarkDeducted = (declarationId: number) => {
    markDeductedModal.open({ id: declarationId, name: `Declaration #${declarationId}` });
  };

  const handleBulkMarkDeducted = () => {
    const pending = deductions.filter((d) => d.payrollStatus !== "deducted");
    bulkMarkDeductedModal.open({
      id: 0,
      name: `${pending.length} pending declaration(s)`,
    });
  };

  const handleExportCSV = () => {
    if (!deductions.length) return;
    const now = new Date();
    const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
    const csvData = [
      ["CAISSE D'ENTRAIDE IPRC KARONGI - MONTHLY SAVINGS DEDUCTIONS"],
      [`Month: ${monthYear}`],
      [`Generated: ${format(now, 'MMMM d, yyyy')} at ${format(now, 'h:mm a')}`],
      [],
      ["Member Code", "Member Name", "Phone", "Declared Amount (FRW)", "Approvals", "Payroll Status"],
      ...deductions.map((d) => [
        d.memberCode || "",
        d.memberName || "",
        d.phone || "",
        d.declaredAmount.toLocaleString(),
        `${d.approvalCount}/3`,
        d.payrollStatus,
      ]),
      [],
      ["SUMMARY"],
      ["Total Members", deductions.length],
      ["Total Amount (FRW)", deductions.reduce((s, d) => s + d.declaredAmount, 0).toLocaleString()],
      [],
    ];
    const csv = csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly_savings_deductions_${monthYear.replace(/\s+/g, "_")}_${format(now, "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Popup blocked. Please allow popups for printing.");
      return;
    }
    const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
    const rows = deductions.map((d, i) => {
      const bg = i % 2 === 0 ? "#f9f9f9" : "#ffffff";
      return `<tr style="background:${bg}">
        <td style="border:1px solid #ddd;padding:8px 6px;">${d.memberCode || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px 6px;">${d.memberName || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px 6px;">${d.phone || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px 6px;text-align:right;">${d.declaredAmount.toLocaleString()} FRW</td>
        <td style="border:1px solid #ddd;padding:8px 6px;text-align:center;">${d.approvalCount}/3</td>
        <td style="border:1px solid #ddd;padding:8px 6px;">${d.payrollStatus.replace(/_/g, " ")}</td>
      </tr>`;
    }).join("");
    printWindow.document.write(`
      <html><head><title>Monthly Savings Deductions</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #000; }
        .header { display: flex; align-items: center; margin-bottom: 20px; }
        .header img { max-width: 100px; margin-right: 20px; }
        h1 { font-size: 20px; margin: 0 0 5px; }
        h2 { font-size: 16px; margin: 0 0 5px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background: #4dbd07; color: white; padding: 10px 6px; text-align: left; border: 1px solid #3d9a06; }
        td { border: 1px solid #ddd; padding: 8px 6px; }
        .summary { margin-top: 20px; padding: 15px; background: #f5f5f5; }
        @media print { body { padding: 20px; } }
      </style></head><body>
        <div class="header">
          <img src="${getCooperativeLogoSrc()}" alt="Logo" />
          <div>
            <h1>CAISSE D'ENTRAIDE IPRC KARONGI</h1>
            <h2>Monthly Savings Deductions</h2>
            <p>${monthYear}</p>
          </div>
        </div>
        <table>
          <thead><tr>
            <th>Member Code</th><th>Member Name</th><th>Phone</th>
            <th style="text-align:right;">Amount</th><th style="text-align:center;">Approval</th><th>Status</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="summary">
          <strong>Total Members:</strong> ${deductions.length} |
          <strong>Total Amount:</strong> ${deductions.reduce((s, d) => s + d.declaredAmount, 0).toLocaleString()} FRW
        </div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleDownloadPDF = async () => {
    if (!deductions.length) return;
    try {
      toast.loading("Generating PDF...", { id: "pdf-generation" });
      const jsPDF = (await import("jspdf")).jsPDF;
      const html2canvas = (await import("html2canvas")).default;
      const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
      const pdfDiv = document.createElement("div");
      pdfDiv.style.background = "#fff";
      pdfDiv.style.padding = "40px";
      pdfDiv.style.fontFamily = "Arial, sans-serif";
      const rows = deductions.map((d, i) => {
        const bg = i % 2 === 0 ? "#f9f9f9" : "#ffffff";
        return `<tr style="background:${bg}">
          <td style="border:1px solid #ddd;padding:8px 6px;">${d.memberCode || "—"}</td>
          <td style="border:1px solid #ddd;padding:8px 6px;">${d.memberName || "—"}</td>
          <td style="border:1px solid #ddd;padding:8px 6px;">${d.phone || "—"}</td>
          <td style="border:1px solid #ddd;padding:8px 6px;text-align:right;">${d.declaredAmount.toLocaleString()} FRW</td>
          <td style="border:1px solid #ddd;padding:8px 6px;text-align:center;">${d.approvalCount}/3</td>
          <td style="border:1px solid #ddd;padding:8px 6px;">${d.payrollStatus.replace(/_/g, " ")}</td>
        </tr>`;
      }).join("");
      pdfDiv.innerHTML = `
        <div style="margin-bottom:30px;">
          <div style="display:flex;align-items:center;margin-bottom:15px;">
            <div style="width:100px;margin-right:20px;"><img src="${getCooperativeLogoSrc()}" style="max-width:100px;" /></div>
            <div><div style="font-size:20px;font-weight:bold;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
            <div style="font-size:16px;font-weight:bold;">Monthly Savings Deductions</div>
            <div>${monthYear}</div></div>
          </div>
          <hr style="border-top:2px solid #000;" />
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead><tr>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;">Code</th>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;">Name</th>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;">Phone</th>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;text-align:right;">Amount</th>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;text-align:center;">Approval</th>
            <th style="background:#4dbd07;color:white;padding:8px 6px;border:1px solid #3d9a06;">Status</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div style="margin-top:20px;padding:15px;background:#f5f5f5;">
          <strong>Total Members:</strong> ${deductions.length} |
          <strong>Total Amount:</strong> ${deductions.reduce((s, d) => s + d.declaredAmount, 0).toLocaleString()} FRW
        </div>
      `;
      document.body.appendChild(pdfDiv);
      const canvas = await html2canvas(pdfDiv, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`monthly_savings_deductions_${monthYear.replace(/\s+/g, "_")}.pdf`);
      document.body.removeChild(pdfDiv);
      toast.dismiss("pdf-generation");
      toast.success("PDF downloaded successfully");
    } catch (err) {
      toast.dismiss("pdf-generation");
      toast.error("Failed to generate PDF");
    }
  };

  const columns = useMemo<ColumnDef<DeductionRow, any>[]>(
    () => [
      {
        accessorKey: "memberCode",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Member ID" />,
        cell: ({ row }) => row.getValue("memberCode") || "—",
      },
      {
        accessorKey: "memberName",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Member Name" />,
        cell: ({ row }) => row.getValue("memberName") || "—",
      },
      {
        accessorKey: "phone",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
        cell: ({ row }) => row.getValue("phone") || "—",
      },
      {
        accessorKey: "declaredAmount",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Amount (FRW)" />,
        cell: ({ row }) => Number(row.getValue("declaredAmount")).toLocaleString(),
      },
      {
        accessorKey: "approvalCount",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approval" />,
        cell: ({ row }) => {
          const count = Number(row.getValue("approvalCount"));
          const color = count >= 3 ? "bg-green-100 text-green-700" : count >= 2 ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700";
          return <Badge className={color}>{count}/3</Badge>;
        },
      },
      {
        accessorKey: "payrollStatus",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const status = row.getValue("payrollStatus") as string;
          const info = PAYROLL_STATUS_LABELS[status] || { label: status, color: "bg-gray-100 text-gray-600" };
          return <Badge className={info.color}>{info.label}</Badge>;
        },
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => {
          const d = row.original;
          if (d.payrollStatus === "deducted") {
            return <Badge className="bg-green-100 text-green-700">Deducted</Badge>;
          }
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMarkDeducted(d.declarationId)}
            >
              <CreditCard className="w-3 h-3 mr-1" />
              Mark Deducted
            </Button>
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-theme-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading monthly savings deductions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-semibold">Failed to load deductions</p>
          <p className="text-muted-foreground mt-2">Please try again later.</p>
        </div>
      </div>
    );
  }

  if (!deductions.length) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
        <p className="text-xl font-semibold mb-2">No payroll deductions found</p>
        <p className="text-muted-foreground mb-4">
          {deductionsData?.batchStatus === "sent_to_hr" || deductionsData?.batchStatus === "locked" || deductionsData?.batchStatus === "processed"
            ? `No declarations are ready for payroll deduction in ${MONTHS[selectedMonth]} ${selectedYear}.`
            : `The payroll list for ${MONTHS[selectedMonth]} ${selectedYear} has not been sent to HR yet. Go to the Contributions page to send it.`}
        </p>
        {deductionsData?.batchStatus !== "sent_to_hr" && deductionsData?.batchStatus !== "locked" && deductionsData?.batchStatus !== "processed" && (
          <Button
            onClick={() => window.location.href = "/contributions"}
            className="bg-[#4dbd07] hover:bg-[#45a806] text-white"
          >
            Go to Contributions
          </Button>
        )}
      </div>
    );
  }

  const totalAmount = deductions.reduce((s, d) => s + d.declaredAmount, 0);
  const pendingCount = deductions.filter((d) => d.payrollStatus !== "deducted").length;

  return (
    <>
      <ConfirmModal
        title="Mark Declaration as Deducted?"
        description={`This will mark declaration #${markDeductedModal.meta?.id} as deducted from salary. A verified contribution record will be created. This action cannot be undone.`}
        meta={markDeductedModal.meta}
        onConfirm={(meta?: any) => {
          const id = Number(meta?.id);
          if (!id) { toast.error("Invalid declaration ID"); return; }
          markSingleMutation.mutate(id);
        }}
        isLoading={markSingleMutation.isLoading}
        open={markDeductedModal.isOpen}
        onClose={() => markDeductedModal.close()}
      />

      <ConfirmModal
        title="Bulk Mark as Deducted?"
        description={`This will mark ${bulkMarkDeductedModal.meta?.name} as deducted from salary. Verified contribution records will be created. This action cannot be undone.`}
        meta={bulkMarkDeductedModal.meta}
        onConfirm={() => bulkMarkMutation.mutate()}
        isLoading={bulkMarkMutation.isLoading}
        open={bulkMarkDeductedModal.isOpen}
        onClose={() => bulkMarkDeductedModal.close()}
      />

      <div className="space-y-4" ref={printRef}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Members</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                {deductions.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Deductions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                {totalAmount.toLocaleString()} FRW
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Payroll Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold">
                <Badge className={
                  deductionsData?.batchStatus === "sent_to_hr" ? "bg-purple-100 text-purple-700" :
                  deductionsData?.batchStatus === "locked" ? "bg-orange-100 text-orange-700" :
                  deductionsData?.batchStatus === "processed" ? "bg-green-100 text-green-700" :
                  "bg-yellow-100 text-yellow-700"
                }>
                  {(deductionsData?.batchStatus || "Unknown").replace(/_/g, " ")}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center justify-between no-print">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
            >
              <Download className="w-4 h-4 mr-1" />
              Export PDF
            </Button>
            {pendingCount > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={handleBulkMarkDeducted}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark All as Deducted ({pendingCount})
              </Button>
            )}
          </div>
        </div>

        <DataTable
          title="Monthly Savings Deductions"
          columns={columns}
          data={deductions}
          isLoading={isLoading}
          defaultColumnVisibility={{}}
          facets={[]}
          onSearch={(query: string) => setSearchQuery(query)}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          sorting={sorting}
          setSorting={setSorting}
          setPagination={setPagination}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          pageCount={Math.ceil(deductions.length / pagination.pageSize)}
          enableExport={false}
        />
      </div>
    </>
  );
}
